/**
 * CRO-Labs (audit 103): "Complete your look" cross-sell in the cart drawer.
 *
 * The host element is server-rendered by snippets/cro-cart-cross-sell.liquid with the first cart
 * product id and the ids already in the cart. The drawer re-renders by morphing, which rewrites
 * host attributes and light-DOM children, so all UI lives in a shadow root (morph never touches
 * it) and JS never writes host attributes. Attribute changes (cart changed) trigger a refresh.
 *
 * Sources in order: recommendations intent=complementary, then intent=related, then the fallback
 * collection. Add posts to /cart/add.js with the cart sections and dispatches the standard
 * CartLinesUpdateEvent, the same way product-form.js does, so the drawer and cart icon update.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const MAX_ITEMS = 3;
/** @type {Map<string, Promise<any>>} */
const responseCache = new Map();

/** @param {string} url */
function cachedJSON(url) {
  if (!responseCache.has(url)) {
    const request = fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
    request.catch(() => responseCache.delete(url));
    responseCache.set(url, request);
  }
  return /** @type {Promise<any>} */ (responseCache.get(url));
}

/** @param {unknown} price */
function toCents(price) {
  if (typeof price === 'number') return price;
  return Math.round(parseFloat(String(price)) * 100);
}

/**
 * Normalizes a product from the recommendations endpoint (.js format, cents) or the
 * collection products.json endpoint (price strings).
 * @param {any} product
 * @param {string} rootUrl
 */
function normalize(product, rootUrl) {
  const variant = (product.variants || []).find((v) => v.available !== false);
  let image = product.featured_image;
  if (image && typeof image === 'object') image = image.src;
  if (!image && product.images && product.images[0]) {
    image = typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
  }
  const root = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
  return {
    id: String(product.id),
    title: String(product.title || ''),
    url: product.url || `${root}products/${product.handle}`,
    image: image || '',
    variantId: variant ? String(variant.id) : '',
    price: variant ? toCents(variant.price) : 0,
    requiresSellingPlan: Boolean(product.requires_selling_plan),
  };
}

/**
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const value = cents / 100;
  /** @param {number} decimals @param {string} thousands @param {string} decimal */
  const fmt = (decimals, thousands, decimal) => {
    const [whole, fraction] = value.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  const text = (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return fmt(0, ',', '.');
      case 'amount_with_comma_separator':
        return fmt(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fmt(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return fmt(2, "'", '.');
      default:
        return fmt(2, ',', '.');
    }
  });
  return text.replace(/<[^>]*>/g, '');
}

/** @param {string} value */
function escapeHTML(value) {
  return value.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** @param {string} src */
function sizedImage(src) {
  if (!src) return '';
  return `${src}${src.includes('?') ? '&' : '?'}width=160`;
}

const STYLES = `
  :host { display: block; }
  .xs { padding-block: 16px 8px; border-top: 1px solid var(--cro-surface); margin-top: 8px; }
  .xs__heading { margin: 0 0 12px; font-size: 14px; font-weight: 600; letter-spacing: 0.02em; color: var(--color-foreground, var(--cro-text)); }
  .xs__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
  .xs__item { display: grid; grid-template-columns: 56px 1fr auto; align-items: center; gap: 12px; }
  .xs__media { display: block; width: 56px; height: 56px; background: var(--cro-surface); overflow: hidden; }
  .xs__media img { display: block; width: 100%; height: 100%; object-fit: cover; }
  .xs__info { min-width: 0; display: grid; gap: 2px; }
  .xs__title { color: var(--color-foreground, var(--cro-text)); text-decoration: none; font-size: 13px; line-height: 1.3;
    overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  .xs__title:hover { text-decoration: underline; }
  .xs__price { font-size: 13px; color: var(--color-foreground, var(--cro-text)); }
  .xs__add { font: inherit; font-size: 13px; font-weight: 600; min-width: 64px; min-height: 36px; padding: 6px 14px;
    border: 1px solid var(--cro-accent); border-radius: 0; background: color-mix(in srgb, var(--cro-accent) 12%, var(--cro-bg)); color: var(--cro-accent); cursor: pointer;
    -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
  .xs__add:hover { background: var(--cro-accent); color: var(--cro-bg); }
  .xs__add:focus-visible { outline: 2px solid var(--cro-accent); outline-offset: 2px; }
  .xs__add[disabled] { opacity: 0.6; cursor: default; }
  .xs__error { margin: 8px 0 0; font-size: 12px; color: var(--cro-accent); }
`;

class CroCartCrossSell extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-exclude'];

  #renderId = 0;
  #busy = false;

  connectedCallback() {
    if (!this.shadowRoot) {
      const root = this.attachShadow({ mode: 'open' });
      root.addEventListener('click', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const button = target.closest ? /** @type {HTMLButtonElement | null} */ (target.closest('.xs__add')) : null;
        if (button) this.#add(button);
      });
    }
    this.#update();
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected || !this.shadowRoot) return;
    this.#update();
  }

  async #update() {
    const renderId = ++this.#renderId;
    let products = [];
    try {
      products = await this.#loadProducts();
    } catch (error) {
      products = [];
    }
    if (renderId !== this.#renderId || !this.shadowRoot) return;
    this.#render(products);
  }

  async #loadProducts() {
    const { productId, exclude = '', recsUrl = '', fallbackUrl = '', rootUrl = '/' } = this.dataset;
    const excluded = new Set(exclude.split(',').filter(Boolean));
    /** @type {string[]} */
    const sources = [];
    // Order: the store's own complementary picks, then the cross-sell collection (add-ons), then related products.
    if (productId && recsUrl) sources.push(`${recsUrl}.json?product_id=${productId}&limit=10&intent=complementary`);
    if (fallbackUrl) sources.push(`${fallbackUrl}/products.json?limit=20`);
    if (productId && recsUrl) sources.push(`${recsUrl}.json?product_id=${productId}&limit=10&intent=related`);

    for (const url of sources) {
      let data;
      try {
        data = await cachedJSON(url);
      } catch (error) {
        continue;
      }
      const products = (data?.products || [])
        .map((/** @type {any} */ p) => normalize(p, rootUrl))
        .filter((/** @type {any} */ p) => p.variantId && !p.requiresSellingPlan && !excluded.has(p.id));
      if (products.length) return products.slice(0, MAX_ITEMS);
    }
    return [];
  }

  /** @param {Array<ReturnType<typeof normalize>>} products */
  #render(products) {
    const root = /** @type {ShadowRoot} */ (this.shadowRoot);
    if (!products.length) {
      root.innerHTML = '';
      return;
    }
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const items = products
      .map((p) => {
        const title = escapeHTML(p.title);
        const url = escapeHTML(p.url);
        const image = p.image
          ? `<img src="${escapeHTML(sizedImage(p.image))}" alt="" width="56" height="56" loading="lazy">`
          : '';
        return `
          <li class="xs__item">
            <a class="xs__media" href="${url}" tabindex="-1" aria-hidden="true">${image}</a>
            <div class="xs__info">
              <a class="xs__title" href="${url}">${title}</a>
              <span class="xs__price">${escapeHTML(formatMoney(p.price, moneyFormat))}</span>
            </div>
            <button type="button" class="xs__add" data-variant-id="${escapeHTML(p.variantId)}" aria-label="Add ${title} to cart">${escapeHTML(this.dataset.addLabel || '')}</button>
          </li>`;
      })
      .join('');
    root.innerHTML = `
      <style>${STYLES}</style>
      <div class="xs">
        <p class="xs__heading" role="heading" aria-level="3">${escapeHTML(this.dataset.heading || '')}</p>
        <ul class="xs__list" role="list">${items}</ul>
        <p class="xs__error" role="alert" hidden></p>
      </div>`;
  }

  /** @param {string} message */
  #showError(message) {
    const error = /** @type {HTMLElement | null} */ (this.shadowRoot?.querySelector('.xs__error'));
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
    setTimeout(() => {
      error.hidden = true;
    }, 4000);
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (this.#busy) return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    this.#busy = true;
    button.disabled = true;
    button.textContent = 'Adding';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const routes = /** @type {any} */ (window).Theme?.routes || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = `${routes.cart_url || '/cart'}.js`;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve(
          /** @type {any} */ ({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'cro-cart-cross-sell', itemCount: 1 },
          })
        );
        this.#showError(data.description || data.message || 'Could not add this item.');
        button.disabled = false;
        button.textContent = this.dataset.addLabel || '';
        return;
      }

      deferred.resolve(
        /** @type {any} */ ({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'cro-cart-cross-sell',
            itemCount: 1,
            sections: data.sections,
            didError: false,
          },
        })
      );
      // The drawer morph updates data-exclude, which re-renders this list without the added product.
      // Safety net in case no re-render happens.
      setTimeout(() => {
        if (!button.isConnected) return;
        button.disabled = false;
        button.textContent = this.dataset.addLabel || '';
      }, 4000);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {Error} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError('Could not add this item. Please try again.');
      button.disabled = false;
      button.textContent = this.dataset.addLabel || '';
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('cro-cart-cross-sell')) {
  customElements.define('cro-cart-cross-sell', CroCartCrossSell);
}
